import React, { useState, useEffect } from 'react';
import { Card, Button, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import Cookies from 'js-cookie';
import axios from 'axios';
import CardViewTable from '../../../components/CardViewTable/CardViewTable';
import { API_BASE_URL as API_BASE } from "../../../config";

function GerenciarClientes() {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentCliente, setCurrentCliente] = useState(null);
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [clienteMasterId, setClienteMasterId] = useState(null);


  useEffect(() => {
    const usuario = JSON.parse(Cookies.get("usuario") || "{}");
    if (usuario && usuario.role === "ClienteMaster") {
      setClienteMasterId(usuario.id);
      fetchClientes(usuario.id);
    } else {
      setError("Acesso negado. Você não é um cliente master.");
      setLoading(false);
    }
  }, []);

  const fetchClientes = async (masterId) => {
    setLoading(true);
    setError(null);
    try {
      const token = Cookies.get("authToken");
      const response = await axios.get(`${API_BASE}/Clientes?idClienteMaster=${masterId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Remove '55' do telefone ao exibir
      const clientesFormatados = response.data.map((c) => {
        let tel = c.telefone || '';
        if (tel.startsWith('55')) tel = tel.slice(2);
        c.telefoneExibicao = tel.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1)$2-$3');
        return c;
      });

      setClientes(clientesFormatados);
    } catch (err) {
      console.error("Erro ao buscar clientes:", err);
      setError("Erro ao carregar clientes.");
    } finally {
      setLoading(false);
    }
  };

  const handleShowAddModal = () => {
    setIsEditing(false);
    setCurrentCliente(null);
    setNome('');
    setTelefone('');
    setEmail('');
    setShowModal(true);
  };

  const handleShowEditModal = (cliente) => {
    setIsEditing(true);
    setCurrentCliente(cliente);
    setNome(cliente.nome || '');
    setEmail(cliente.email || '');

    // Remove '55' do início do telefone e aplica máscara
    let telefoneExibicao = cliente.telefone || '';
    if (telefoneExibicao.startsWith('55')) telefoneExibicao = telefoneExibicao.slice(2);
    const telefoneFormatado = telefoneExibicao.replace(
      /^(\d{2})(\d{5})(\d{4})$/,
      '($1)$2-$3'
    );
    setTelefone(telefoneFormatado);

    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const token = Cookies.get("authToken");
      const telefoneDigits = telefone.replace(/\D/g, '');
      const telefoneComDDI = telefoneDigits.length === 11 ? '55' + telefoneDigits : telefoneDigits;

      const clienteData = {
        nome,
        telefone: telefoneComDDI,
        email,
        clienteMasterId,
      };

      if (isEditing && currentCliente) {
        await axios.put(`${API_BASE}/Clientes/${currentCliente.id}`, clienteData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${API_BASE}/Clientes`, clienteData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      fetchClientes(clienteMasterId);
      handleCloseModal();
    } catch (err) {
      console.error("Erro ao salvar cliente:", err);
      setError("Erro ao salvar cliente.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!(await window.globalConfirm("Tem certeza que deseja excluir este cliente?"))) return;
    setSubmitting(true);
    setError(null);
    try {
      const token = Cookies.get("authToken");
      await axios.delete(`${API_BASE}/Clientes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchClientes(clienteMasterId);
    } catch (err) {
      console.error("Erro ao excluir cliente:", err);
      setError("Erro ao excluir cliente.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Spinner animation="border" />;
  if (error) return <Alert variant="danger">{error}</Alert>;

  const cardStyle = {
    backgroundColor: '#3c4049',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    padding: '25px',
    width: '100%',
    maxWidth: '100%',
    margin: '0',
  };

  return (
    <Card className="shadow-lg" style={cardStyle}>
      <Card.Title className="mb-4 fs-3 text-center">Gerenciar Clientes</Card.Title>
      <Card.Body>
        <Button variant="primary" onClick={handleShowAddModal} className="mb-3">
          Adicionar Cliente
        </Button>

        {clientes.length > 0 ? (
          <CardViewTable
            data={clientes}
            keyField="id"
            cardTitleKey="nome"
            columns={[
              { key: 'nome', header: 'Nome' },
              { key: 'telefoneExibicao', header: 'Telefone' },
              { key: 'actions', header: 'Ações' },
            ]}
            actions={(cliente) => (
              <div className="d-flex flex-wrap gap-2">
                <Button
                  variant="info"
                  size="sm"
                  onClick={() => handleShowEditModal(cliente)}
                  className="flex-grow-1"
                >
                  Editar
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDelete(cliente.id)}
                  className="flex-grow-1"
                >
                  Excluir
                </Button>
              </div>
            )}
          />
        ) : (
          <Alert variant="info" className="text-center mb-0">
            Nenhum cliente encontrado.
          </Alert>
        )}

        <Modal show={showModal} onHide={handleCloseModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>{isEditing ? 'Editar Cliente' : 'Adicionar Cliente'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Nome</Form.Label>
                <Form.Control
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Telefone (DDD + Número)</Form.Label>
                <Form.Control
                  type="text"
                  value={telefone}
                  onChange={(e) => {
                    let val = e.target.value.replace(/\D/g, '');
                    if (val.length > 11) val = val.slice(0, 11);
                    const masked = val
                      .replace(/^(\d{2})(\d{5})(\d{0,4})$/, '($1)$2-$3')
                      .replace(/-$/, '');
                    setTelefone(masked);
                  }}
                  placeholder="(11)98765-4321"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Form.Group>

              <div className="d-flex justify-content-end">
                <Button variant="primary" type="submit" disabled={submitting}>
                  {submitting ? <Spinner animation="border" size="sm" /> : 'Salvar'}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      </Card.Body>
    </Card>
  );
}

export default GerenciarClientes;
